import React, { useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { alpha, createTheme, ThemeProvider } from '@material-ui/core/styles';
import getTheme from 'theme';
import Link from '@material-ui/core/Link';
import ComputerIcon from '@material-ui/icons/Computer';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import GroupsIcon from '@material-ui/icons/Groups';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import MicIcon from '@material-ui/icons/Mic';
import MovieCreationIcon from '@material-ui/icons/MovieCreation';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import PsychologyIcon from '@material-ui/icons/Psychology';
import SearchIcon from '@material-ui/icons/Search';
import SmartToyIcon from '@material-ui/icons/SmartToy';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';

/** Fondo Press Start: base + capas (sin oro en el degradado; el calor lo dan los flares ::before/::after). */
const PRESS_START_FALLBACK_BG = '#02040c';
const PRESS_START_PAGE_BG = [
  'radial-gradient(ellipse 108% 58% at 50% -4%, rgba(88,102,220,0.24) 0%, transparent 52%)',
  'radial-gradient(ellipse 78% 58% at 10% 94%, rgba(2,3,10,0.82) 0%, transparent 54%)',
  'linear-gradient(124deg, #02040c 0%, #060a1c 20%, #0c1230 44%, #141b42 68%, #1b2454 100%)',
].join(', ');
const BG = 'rgba(8, 11, 28, 0.97)';
const BG_SOFT = 'rgba(18, 22, 46, 0.94)';

/** Oscuro para proyector; acentos = primary de la paleta que elija el usuario en la barra. */
function createPressDeckTheme(themeMode, paletteType) {
  const base = getTheme(themeMode, paletteType);
  return createTheme(base, {
    palette: {
      ...base.palette,
      mode: 'dark',
      background: {
        default: BG,
        paper: BG_SOFT,
      },
      text: {
        primary: '#f8fafc',
        secondary: 'rgba(248, 250, 252, 0.82)',
        disabled: 'rgba(248, 250, 252, 0.45)',
      },
      divider: alpha(base.palette.primary.main, 0.28),
    },
    typography: {
      ...base.typography,
      fontSize: Math.round((base.typography?.fontSize ?? 14) * 1.12),
      body1: {
        ...base.typography.body1,
        fontSize: '1.08rem',
        lineHeight: 1.75,
      },
      body2: {
        ...base.typography.body2,
        fontSize: '0.98rem',
        lineHeight: 1.65,
      },
      overline: {
        ...base.typography.overline,
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },
    },
  });
}

const BrandTnoCon = ({ sxTno = {}, sxCon = {} }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap', display: 'inline' }}>
    <Box
      component="span"
      sx={{
        fontWeight: 900,
        letterSpacing: '-0.07em',
        color: 'common.white',
        textShadow:
          '0.02em 0 0 currentColor, -0.02em 0 0 currentColor, 0 0.02em 0 currentColor, 0 -0.02em 0 currentColor',
        ...sxTno,
      }}
    >
      TNO
    </Box>
    <Box
      component="span"
      sx={{
        fontWeight: 200,
        letterSpacing: '-0.07em',
        marginLeft: '-0.06em',
        color: 'rgba(255,255,255,0.72)',
        ...sxCon,
      }}
    >
      CON
    </Box>
  </Box>
);

function allyImgSrc(path) {
  if (!path) return '';
  const raw = typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '';
  if (!raw || raw === '/') return path;
  const base = raw.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

const teamRows = [
  { role: 'Dirección General', names: 'Karolyn Méndez' },
  { role: 'Dirección de Información', names: 'Sol Vargas' },
  { role: 'Coordinación General', names: 'Yanira Bocaranda' },
  { role: 'Dirección de Comercialización', names: 'Jaqueline Arteaga' },
  { role: 'Coordinación Técnica', names: 'Doubraska Anais' },
  { role: 'Operación Técnica', names: 'Marco Casique, Karla Castillo' },
  { role: 'Coordinación de Producción', names: 'Yelitza Yánez, Margarita Toledo, Angélica Lugo' },
  { role: 'Asistencia de Producción', names: 'Mariana Osuna, Aleska Pin' },
];

const sectionDividerSx = {
  borderBottom: '1px solid',
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
};

const heroSectionSx = {
  ...sectionDividerSx,
  minHeight: { xs: '85vh', md: '88vh' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: { xs: 2.5, sm: 4, md: 6 },
  py: { xs: 6, md: 8 },
  boxSizing: 'border-box',
  textAlign: 'center',
};

const sectionShellSx = {
  ...sectionDividerSx,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  px: { xs: 2.5, sm: 4, md: 6 },
  py: { xs: 6, md: 9 },
  boxSizing: 'border-box',
};

const cueNoteSx = {
  mt: 2.5,
  p: { xs: 2, md: 2.5 },
  borderRadius: 2,
  bgcolor: BG_SOFT,
  border: '1px solid',
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
  maxWidth: 820,
  width: '100%',
};

const TNO_SITE = 'https://tnonetwork.com';
const ADS_MEDIA_KIT_URL = 'https://ads.tnonetwork.com';
const TNO_NETWORK_LOGO_WHITE = '/images/logos/logo_white.PNG';

function PressStartPresentation({ themeMode = 'light', paletteType = 'blue' }) {
  const deckTheme = useMemo(() => createPressDeckTheme(themeMode, paletteType), [themeMode, paletteType]);

  return (
    <ThemeProvider theme={deckTheme}>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'auto',
          overflowX: 'hidden',
          color: 'text.primary',
          bgcolor: PRESS_START_FALLBACK_BG,
          backgroundImage: PRESS_START_PAGE_BG,
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          /* Halos / flares — mismo lenguaje que LandingBusinessLevel */
          '&:before': {
            content: '" "',
            position: 'fixed',
            top: { xs: -100, md: -120 },
            right: { xs: -100, md: -120 },
            width: { xs: 280, md: 360 },
            height: { xs: 280, md: 360 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,214,120,0.5), rgba(201,162,39,0.28) 38%, rgba(201,162,39,0))',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&:after': {
            content: '" "',
            position: 'fixed',
            bottom: { xs: -80, md: -100 },
            left: { xs: -60, md: -80 },
            width: { xs: 240, md: 300 },
            height: { xs: 240, md: 300 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(201,162,39,0.22), rgba(201,162,39,0))',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pt: { xs: 2.5, md: 3.5 },
              pb: { xs: 0.5, md: 1 },
              px: 2,
            }}
          >
            <Box
              component="img"
              src={allyImgSrc(TNO_NETWORK_LOGO_WHITE)}
              alt="TNO Network"
              sx={{
                display: 'block',
                maxWidth: { xs: 200, sm: 260, md: 320 },
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
          {/* 1 — Bienvenida (guión Sol) */}
          <Box sx={heroSectionSx}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                letterSpacing: { xs: 3, md: 5 },
                fontWeight: 800,
                fontSize: { xs: '0.82rem', md: '1.02rem' },
                mb: 2,
                display: 'block',
                lineHeight: 1.5,
              }}
            >
              <BrandTnoCon /> 2026 · Press Start · Convención TNO
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.1rem', sm: '2.72rem', md: '3.65rem', lg: '4.2rem' },
                lineHeight: 1.12,
                color: 'common.white',
                maxWidth: 1100,
                mx: 'auto',
              }}
            >
              La evolución del ecosistema de contenidos
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: 'primary.main',
                fontSize: { xs: '1.06rem', md: '1.24rem' },
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              TNO 2026: El Despegue Digital
            </Typography>
            <Typography
              sx={{
                mt: { xs: 2.5, md: 3 },
                color: 'rgba(255,255,255,0.85)',
                fontSize: { xs: '1.12rem', md: '1.28rem' },
                lineHeight: 1.75,
                maxWidth: 920,
                mx: 'auto',
              }}
            >
              Agradecidos por su presencia. Presentamos la evolución del ecosistema de contenidos con la señal multiplataforma{' '}
              <Box component="span" sx={{ fontWeight: 800, color: 'common.white' }}>
                TNO Network
              </Box>
              , el universo del{' '}
              <Box component="span" sx={{ fontWeight: 800, color: 'common.white' }}>
                Media Lab
              </Box>{' '}
              y el alcance a audiencias globales.
            </Typography>
            <Typography
              sx={{
                mt: { xs: 3, md: 4 },
                color: 'rgba(255,255,255,0.78)',
                fontSize: { xs: '1.12rem', md: '1.38rem' },
                fontWeight: 500,
              }}
            >
              Martes 21 de abril · Las Mercedes, Caracas
            </Typography>
          </Box>

          {/* 2 — Cabina en vivo + CTA web (guión) */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <MicIcon sx={{ fontSize: 46, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Conexión en vivo
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.7rem', md: '2.38rem' },
                color: 'common.white',
                mb: 2,
                maxWidth: 900,
              }}
            >
              Cabina de grabación y espacios en vivo
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.75, maxWidth: 760, fontSize: { xs: '1.12rem', md: '1.22rem' } }}>
              Contacto con nuestra <strong>CEO de TNO Network, Karolyn Méndez</strong>. La prensa puede seguir la señal desde su teléfono.
            </Typography>
            <Box sx={{ ...cueNoteSx, mt: 3, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', mb: 1, fontSize: '1.02rem' }}>Conéctese a</Typography>
              <Link
                href={TNO_SITE}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.52rem' }, color: 'primary.main' }}
              >
                tnonetwork.com
              </Link>
            </Box>
            <Typography sx={{ ...cueNoteSx, mt: 2, color: 'rgba(255,255,255,0.65)', fontStyle: 'italic', fontSize: '1.02rem' }}>
              Pase a cabina · intervención de Karolyn · cierre y retorno a sala con Sol
            </Typography>
          </Box>

          {/* 3 — Ecosistema */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch' }}>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: { xs: 'center', md: 'flex-start' } }}
            >
              Ecosistema multiplataforma
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.86rem', md: '2.65rem' },
                color: 'common.white',
                mb: { xs: 4, md: 5 },
                textAlign: { xs: 'center', md: 'left' },
                alignSelf: { xs: 'center', md: 'flex-start' },
              }}
            >
              Tres superficies, una misma señal
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 3, md: 2 },
                width: '100%',
                maxWidth: 1000,
                mx: 'auto',
              }}
            >
              {[
                { Icon: LiveTvIcon, title: 'Streaming', line: 'Conexión en tiempo real.' },
                { Icon: VideoLibraryIcon, title: 'On-Demand', line: 'Tu contenido, a tu ritmo.' },
                { Icon: MenuBookIcon, title: 'Digest', line: 'La profundidad de la palabra.' },
              ].map(({ Icon, title, line }, i) => (
                <React.Fragment key={title}>
                  <Box
                    sx={{
                      flex: { md: '1 1 0' },
                      textAlign: 'center',
                      p: { xs: 2.5, md: 3 },
                      borderRadius: 2,
                      bgcolor: BG_SOFT,
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                      width: { xs: '100%', md: 'auto' },
                      maxWidth: 320,
                    }}
                  >
                    <Icon sx={{ fontSize: 54, color: 'primary.main', mb: 1.5 }} />
                    <Typography sx={{ fontWeight: 800, color: 'common.white', fontSize: '1.3rem', mb: 1 }}>{title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.55, fontSize: '1.06rem' }}>{line}</Typography>
                  </Box>
                  {i < 2 && (
                    <DeviceHubIcon
                      sx={{
                        color: (theme) => alpha(theme.palette.primary.main, 0.45),
                        fontSize: { xs: 32, md: 42 },
                        transform: { xs: 'rotate(90deg)', md: 'none' },
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>

          {/* 4 — Video transición (piso AV) */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <PlayCircleOutlineIcon sx={{ fontSize: 54, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Transición
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.68rem', md: '2.3rem' }, color: 'common.white', mb: 1.5, lineHeight: 1.2 }}>
              Demo reel
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.96rem', letterSpacing: 1, mb: 2 }}>
              Pieza de cabina · «12 años de TNO Network»
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.75, maxWidth: 720 }}>
              Mientras Karolyn regresa a la sala: cápsula audiovisual con lo mejor del <strong>sonido en vivo</strong>, la <strong>cabina</strong> y el trabajo en antena de TNO Network.
            </Typography>
          </Box>

          {/* 5 — Momento web */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <ComputerIcon sx={{ fontSize: 50, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Proyección
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.74rem', md: '2.36rem' }, color: 'common.white', mb: 2 }}>
              Sitio web en monitores
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, maxWidth: 760 }}>
              Sol presenta a <strong>Karolyn Méndez</strong>, quien guía la navegación en vivo sobre la página y las imágenes de la plataforma.
            </Typography>
          </Box>

          {/* 6 — Plantilla de talentos */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 50, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Talento en vivo
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.74rem', md: '2.36rem' }, color: 'common.white', mb: 2 }}>
              Plantilla de talentos
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, maxWidth: 780, mb: 2 }}>
              Cada talento: <strong>nombre</strong>, <strong>identificación del programa</strong> y <strong>su contenido</strong>.
            </Typography>
            <Box sx={{ ...cueNoteSx, textAlign: 'left' }}>
              <Typography sx={{ color: 'common.white', fontWeight: 800, mb: 1 }}>Formato</Typography>
              <Typography component="ul" sx={{ m: 0, pl: 2.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.85 }}>
                <li>Hasta <strong>1 minuto</strong> por intervención.</li>
                <li>
                  <strong>Sol Vargas</strong> cierra el bloque como última voz con <strong>«Noticias TNO»</strong>.
                </li>
              </Typography>
            </Box>
          </Box>

          {/* 7 — Reels */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <MovieCreationIcon sx={{ fontSize: 50, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Proyección
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.74rem', md: '2.36rem' }, color: 'common.white', mb: 2 }}>
              Reels de programas y estrenos 2026
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.75, maxWidth: 720 }}>
              Tras el bloque de talentos: pie audiovisual con reels de la grilla TNO y adelanto de estrenos del año.
            </Typography>
          </Box>

          {/* 8 — Equipo */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch', maxWidth: 1080, mx: 'auto', width: '100%' }}>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              El corazón de TNO
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.86rem', md: '2.65rem' },
                color: 'common.white',
                mb: { xs: 3, md: 4 },
              }}
            >
              Equipo de trabajo
            </Typography>
            <Grid container spacing={1.5}>
              {teamRows.map((row) => (
                <Grid item xs={12} sm={6} key={row.role}>
                  <Box
                    sx={{
                      height: '100%',
                      p: { xs: 1.75, md: 2 },
                      borderRadius: 1.5,
                      bgcolor: BG_SOFT,
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.92rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {row.role}
                    </Typography>
                    <Typography sx={{ color: 'common.white', mt: 0.75, fontWeight: 600, lineHeight: 1.45, fontSize: '1.06rem' }}>{row.names}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* 9 — Palabras de cierre y pie de salida (guión) */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Palabras de cierre
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.74rem', md: '2.36rem' }, color: 'common.white', mb: 2 }}>
              Karolyn Méndez
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, maxWidth: 760, mb: 2 }}>
              Las <strong>palabras de cierre</strong> y el <strong>pie de salida</strong> a la sala las pronuncia <strong>Karolyn Méndez</strong>, Dirección General de TNO Network.
            </Typography>
            <Box sx={{ ...cueNoteSx, textAlign: 'left' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.75 }}>
                Sol presenta a Karolyn para ese cierre; al terminar, <strong>Sol retoma</strong> y anuncia el <strong>ciclo de preguntas</strong> con la prensa.
              </Typography>
            </Box>
          </Box>

          {/* 10 — Smart Tech */}
          <Box sx={sectionShellSx}>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: 'center' }}>
              Innovación y roadmap 2026
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.86rem', md: '2.65rem' },
                color: 'common.white',
                mb: { xs: 4, md: 5 },
                textAlign: 'center',
              }}
            >
              Smart Tech
            </Typography>
            <Grid container spacing={2.5} sx={{ maxWidth: 960, width: '100%' }}>
              {[
                {
                  Icon: SmartToyIcon,
                  title: 'IA de navegación',
                  text: 'Chatbot de asistencia al usuario.',
                },
                {
                  Icon: PsychologyIcon,
                  title: 'Algoritmos propios',
                  text: 'Recomendación personalizada de contenido.',
                },
                {
                  Icon: SearchIcon,
                  title: 'Estrategia SEO',
                  text: 'Mayor visibilidad para nuestros talentos y aliados.',
                },
              ].map(({ Icon, title, text }) => (
                <Grid item xs={12} md={4} key={title}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: BG_SOFT,
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                      textAlign: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 50, color: 'primary.main', mb: 1.5 }} />
                    <Typography sx={{ fontWeight: 800, color: 'common.white', mb: 1, fontSize: '1.18rem' }}>{title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontSize: '1.07rem' }}>{text}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* 12 — Cierre del acto */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Después de la rueda
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.68rem', md: '2.26rem' }, color: 'common.white', mb: 2 }}>
              Ciclo de preguntas · cara a cara · refrigerio
            </Typography>
            <Box sx={{ ...cueNoteSx, textAlign: 'left' }}>
              <Typography component="ul" sx={{ m: 0, pl: 2.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.85 }}>
                <li>
                  <strong>Ciclo de preguntas</strong> con la prensa.
                </li>
                <li>
                  Tras la <strong>última pregunta</strong>: cierre formal de la rueda de prensa e inicio de los <strong>cara a cara</strong>.
                </li>
                <li>
                  <strong>Refrigerio</strong> para acompañar el networking.
                </li>
              </Typography>
            </Box>
          </Box>

          {/* 13 — Media Kit */}
          <Box
            sx={{
              ...sectionShellSx,
              textAlign: 'center',
              borderBottom: 'none',
              pb: { xs: 10, md: 14 },
            }}
          >
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 2 }}>
              Media Kit 2026
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.68rem', sm: '2.24rem', md: '2.8rem' },
                lineHeight: 1.25,
                color: 'common.white',
                maxWidth: 720,
                mx: 'auto',
              }}
            >
              <Link
                href={ADS_MEDIA_KIT_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                  wordBreak: 'break-word',
                }}
              >
                https://ads.tnonetwork.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default PressStartPresentation;
